import { getAllGeofences } from "../../api/new-api";
import { EditableFence, LatLng } from "./types";

/**
 * Fetch every geofence and convert it to the editable shape used by the map.
 * Throws on failure so the caller can surface the error to the user.
 */
export const fetchFences = async (): Promise<EditableFence[]> => {
  const res = await getAllGeofences();

  return res.data.geofences.map((fence, index) => ({
    locationId: fence.locationId,
    name: fence.name,
    status: "ACTIVE",
    index,
    coordinates: fence.coordinates.map((coord) => ({
      lat: coord.latitude,
      lng: coord.longitude,
    })),
  }));
};

const temporaryId = () => "_" + Math.random().toString(36).substring(2, 9);

/** Wrap freshly drawn coordinates as an unsaved fence. */
export const createFence = (
  coordinates: LatLng[],
  index: number
): EditableFence => ({
  locationId: temporaryId(),
  name: "",
  status: "NEW",
  index,
  coordinates,
});

/** Fraction of the visible map each side of the quick square spans. */
const SQUARE_VIEWPORT_FRACTION = 0.15;

/**
 * A square centred in the current view and sized relative to it, so it is
 * always visible and draggable at whatever zoom the user is on. Returns null
 * until the map has reported its bounds.
 */
export const createSquareInView = (
  map: google.maps.Map,
  index: number
): EditableFence | null => {
  const bounds = map.getBounds();
  const center = map.getCenter();
  if (!bounds || !center) return null;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const halfLat = ((ne.lat() - sw.lat()) * SQUARE_VIEWPORT_FRACTION) / 2;
  const halfLng = ((ne.lng() - sw.lng()) * SQUARE_VIEWPORT_FRACTION) / 2;
  const lat = center.lat();
  const lng = center.lng();

  return createFence(
    [
      { lat: lat - halfLat, lng: lng + halfLng },
      { lat: lat + halfLat, lng: lng + halfLng },
      { lat: lat + halfLat, lng: lng - halfLng },
      { lat: lat - halfLat, lng: lng - halfLng },
    ],
    index
  );
};

/**
 * Shape sent to the API. The server returns fences with the first point repeated
 * at the end to close the ring; sending that back (or sending it for a brand new
 * fence) makes create and update payloads differ, so both go through here.
 */
export const normaliseCoordinates = (coords: LatLng[]): LatLng[] => {
  const seen = new Set<string>();

  return coords.filter((coord) => {
    const key = `${coord.lat},${coord.lng}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const isSameShape = (a: LatLng[], b: LatLng[]): boolean =>
  a.length === b.length &&
  a.every((p, i) => p.lat === b[i].lat && p.lng === b[i].lng);

/** Arithmetic centre. Good enough for building-sized fences used as a label anchor. */
export const centroidOf = (coords: LatLng[]): LatLng => {
  const n = coords.length || 1;
  const sum = coords.reduce(
    (acc, c) => ({ lat: acc.lat + c.lat, lng: acc.lng + c.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / n, lng: sum.lng / n };
};

/** Read a Google polygon's current path as plain coordinates. */
export const readPolygonPath = (polygon: google.maps.Polygon): LatLng[] =>
  polygon
    .getPath()
    .getArray()
    .map((point) => ({ lat: point.lat(), lng: point.lng() }));

/** Area in square metres, or null if the geometry library is unavailable. */
export const computeAreaM2 = (coords: LatLng[]): number | null => {
  const spherical = window.google?.maps?.geometry?.spherical;
  if (!spherical || coords.length < 3) return null;
  return spherical.computeArea(coords);
};

export const formatArea = (areaM2: number): string =>
  areaM2 >= 1_000_000
    ? `${(areaM2 / 1_000_000).toFixed(2)} km²`
    : `${Math.round(areaM2).toLocaleString()} m²`;

/** Grow bounds by `factor` of their span on every side, so panning a little doesn't pop fences in and out. */
export const expandBounds = (
  bounds: google.maps.LatLngBounds,
  factor: number
): google.maps.LatLngBounds => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const dLat = (ne.lat() - sw.lat()) * factor;
  const dLng = (ne.lng() - sw.lng()) * factor;

  return new google.maps.LatLngBounds(
    { lat: sw.lat() - dLat, lng: sw.lng() - dLng },
    { lat: ne.lat() + dLat, lng: ne.lng() + dLng }
  );
};

/** True if any corner of the fence falls inside `bounds`. Fences are small, so corners are enough. */
export const fenceIntersects = (
  fence: EditableFence,
  bounds: google.maps.LatLngBounds
): boolean => fence.coordinates.some((coord) => bounds.contains(coord));
